import { sanitizeHtml } from '../lib/sanitize';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Loader2, Lock, ArrowLeft } from 'lucide-react';
import '../styles/reader.css';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Secure chapter reader with:
 *  - left sidebar TOC (H2/H3 of the chapter) with scroll-spy
 *  - inline rendering of the H1 + descendants (assembled in order)
 *  - tiled diagonal watermark (full name + date)
 *  - copy/cut/right-click/drag/print blockers
 */
export const CourseReaderPage = () => {
  const { lang = 'fr', slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const contentRef = useRef(null);

  // Fetch chapter + descendants
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const res = await axios.get(`${API}/api/course-content/chapter/${slug}`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Accès interdit.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Anti-copy / anti-screenshot deterrents
  useEffect(() => {
    if (!data) return;
    const block = (e) => { e.preventDefault(); return false; };
    const onKeyDown = (e) => {
      const k = e.key?.toLowerCase();
      // Block Ctrl+C / Ctrl+X / Ctrl+P / Ctrl+S / Ctrl+U
      if ((e.ctrlKey || e.metaKey) && ['c', 'x', 'p', 's', 'u', 'a'].includes(k)) {
        e.preventDefault();
        return false;
      }
      // Block Print Screen / F12 / F11
      if (['printscreen', 'f12'].includes(k)) {
        e.preventDefault();
        navigator.clipboard?.writeText('').catch(() => {});
      }
    };
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', block);
    document.addEventListener('dragstart', block);
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('overflow-x-hidden');
    return () => {
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('dragstart', block);
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('overflow-x-hidden');
    };
  }, [data]);

  // Scroll-spy: highlight current section in sidebar
  const handleScroll = useCallback(() => {
    if (!contentRef.current) return;
    const sections = contentRef.current.querySelectorAll('[data-anchor]');
    let current = null;
    const top = window.scrollY + 140;
    sections.forEach((s) => {
      const offset = s.getBoundingClientRect().top + window.scrollY;
      if (offset <= top) current = s.dataset.anchor;
    });
    setActiveId(current);
  }, []);

  useEffect(() => {
    if (!data) return;
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data, handleScroll]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F0]">
      <Loader2 className="animate-spin text-[#1F2A4A]" />
    </div>
  );

  if (error) return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[#FAF7F0] px-6">
      <div className="text-center max-w-md">
        <Lock className="mx-auto text-[#C9B896] mb-4" size={40} />
        <h1 className="font-serif text-3xl text-[#1F2A4A] font-light mb-3">Lecture restreinte</h1>
        <p className="text-[#4A5568]">{error}</p>
        <Link to={`/${lang}/cours-en-ligne`} className="mt-6 inline-block text-sm underline text-[#1F2A4A]">
          Retour à la bibliothèque
        </Link>
      </div>
    </div>
  );

  const { chapter, descendants = [], watermark } = data;
  const tocItems = descendants.filter((d) => d.level <= 3);

  // Build watermark tile content
  const wmLabel = `${watermark?.label || 'Sellerie Académie'} · ${watermark?.date || ''}`;
  const tileTexts = new Array(80).fill(wmLabel);

  return (
    <div className="min-h-screen bg-[#FAF7F0] sa-reader-protected" data-testid="course-reader">
      {/* Watermark overlay */}
      <div className="sa-watermark-tile" aria-hidden="true">
        <div className="sa-watermark-tile-inner">
          {tileTexts.map((t, i) => <span key={i}>{t}</span>)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-10">
        {/* Sidebar TOC */}
        <aside className="md:col-span-3 md:sticky md:top-24 self-start" data-testid="reader-toc">
          <Link to={`/${lang}/cours-en-ligne`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#4A5568] hover:text-[#1F2A4A] mb-6">
            <ArrowLeft size={14} /> Bibliothèque
          </Link>
          <p className="text-[10px] font-mono tracking-[0.28em] text-[#C9B896] mb-3">SOMMAIRE</p>
          <h3 className="font-serif text-lg text-[#1F2A4A] font-light leading-tight mb-5">
            {chapter.title}
          </h3>
          {tocItems.length > 0 ? (
            <ul className="text-sm space-y-1 border-l border-[#C9B896]/40">
              {tocItems.map((s) => (
                <li
                  key={s.id}
                  style={{ paddingLeft: `${(s.level - 1) * 12}px` }}
                  className={`pl-3 -ml-px border-l-2 cursor-pointer py-1.5 transition-colors ${
                    activeId === s.slug
                      ? 'border-[#1F2A4A] text-[#1F2A4A] font-semibold'
                      : 'border-transparent text-[#4A5568] hover:text-[#1F2A4A]'
                  }`}
                  onClick={() => {
                    document.querySelector(`[data-anchor="${s.slug}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid={`toc-${s.slug}`}
                >
                  {s.title}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[#4A5568] italic">Section unique — pas de sous-chapitres.</p>
          )}
        </aside>

        {/* Reader content */}
        <article ref={contentRef} className="md:col-span-9 bg-white rounded-2xl p-8 md:p-14 shadow-sm sa-reader-prose">
          <header className="mb-10 pb-6 border-b border-[#C9B896]/40">
            <p className="text-xs font-mono tracking-[0.3em] text-[#C9B896] mb-3">CHAPITRE</p>
            <h1 className="font-serif text-3xl md:text-5xl text-[#1F2A4A] font-light leading-tight">{chapter.title}</h1>
          </header>

          <section data-anchor={chapter.slug} dangerouslySetInnerHTML={{ __html: sanitizeHtml(chapter.content_html) || '<p class="italic text-[#4A5568]">Pas de contenu pour le chapitre principal.</p>' }} />

          {descendants.map((d) => (
            <section key={d.id} data-anchor={d.slug} className="mt-10">
              {d.level === 2 && <h2>{d.title}</h2>}
              {d.level === 3 && <h3>{d.title}</h3>}
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(d.content_html) }} />
            </section>
          ))}

          <footer className="mt-16 pt-6 border-t border-[#C9B896]/20 text-xs text-[#4A5568] flex items-center justify-between">
            <span>© Sellerie Académie — Tout droits réservés</span>
            <span className="flex items-center gap-1.5"><Lock size={11} /> Lecture protégée pour {watermark?.label}</span>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default CourseReaderPage;
